import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { TrendingUp, AlertTriangle, Zap, Clock } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  loadFactor: { label: "Load Factor", color: CHIP_COLORS.info },
};

const ITEMS_PER_PAGE = 10;

const PeakAnalysis = () => {
  const { reports, loading, error, progress } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports)
    return <div className="p-10 text-center">No data available</div>;

  const { demand } = reports;
  const { hourlyLoadFactor, perMeter } = demand;

  // Hourly load factor chart
  const loadFactorTrend = hourlyLoadFactor.map((d) => ({
    time: d.hour,
    loadFactor: Number(d.value).toFixed(2),
    label: d.hour,
  }));

  // Pagination
  const currentPage = getPage("meters");
  const totalPages = Math.ceil(perMeter.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeter.length);
  const paginatedMeters = perMeter.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">System Peak</p>
                <p className="text-lg font-bold">
                  {demand.peakKW.toLocaleString()} kW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Clock className="w-4 h-4 text-info" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Peak Time</p>
                <p className="text-lg font-bold">
                  {new Date(demand.peakTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Zap className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Load Factor</p>
                <p className="text-lg font-bold">{demand.loadFactor}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <AlertTriangle className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Contract Load</p>
                <p className="text-lg font-bold">
                  {demand.contractLoad.toLocaleString()} kW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-12">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Hourly Load Factor Analysis
                </CardTitle>
                <CardDescription>
                  System efficiency throughout the day
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Efficiency
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={loadFactorTrend}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorLoad" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={CHIP_COLORS.info}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={CHIP_COLORS.info}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.5}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={30}
                  />
                  <YAxis
                    domain={[0, 1]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-xs mb-2">
                              {payload[0].payload.label}
                            </p>
                            <p className="text-sm font-medium">
                              Load Factor: {payload[0].value}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="loadFactor"
                    stroke={CHIP_COLORS.info}
                    fillOpacity={1}
                    fill="url(#colorLoad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Per-Meter Peak Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Peak Analysis
              </CardTitle>
              <CardDescription>
                Detailed peak load data for {perMeter.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Peak Summary" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-right">Peak kW</TableHead>
                  <TableHead className="text-right">Avg kW</TableHead>
                  <TableHead className="text-right">Load Factor</TableHead>
                  {/* <TableHead className="text-right">MD Resets</TableHead> */}
                  <TableHead className="text-right">Peak Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {meter.maxKW}
                    </TableCell>
                    <TableCell className="text-right">{meter.avgKW}</TableCell>
                    <TableCell className="text-right">
                      {meter.loadFactor}
                    </TableCell>
                    {/* <TableCell className="text-right">
                      {meter.mdResets}
                    </TableCell> */}
                    <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                      {meter.peakTime}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeter.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default PeakAnalysis;
